/* eslint-disable no-empty */
import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import axios from '../../../../config/axios'
import { ChevronDown, ChevronUp, Info, Plus } from 'lucide-react'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import AttachmentManager, { ExistingAttachment } from '../../../../components/AttachmentManager'
import {
  Card,
  CollapsibleHeader,
  Separator,
  Title,
  Button,
  AddButton,
  AddButtonImg,
  AddButtonText,
  AddProductContainer,
  FormGroup,
  Label,
  InputText,
  Select,
  FormGrid,
  BreadCrumble,
  FirstText,
  SecondText,
  InfoLink,
  Summary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  ProductShort,
  ProductDiv,
  ShortLabel,
  ActionsDiv,
  ProductFieldLabel,
  ProductFieldsContainer,
} from './style'
import AddProductForm from '../AddProductForm'
import CompanySelect from '../SelectItem/CompanySelect'
import VendorSelect from '../SelectItem/VendorSelect'
import plus from '../../../../assets/fashion/TableFashionHeader/plus.svg'
import rectangle from '../../../../assets/fashion/TableFashionHeader/rectangle.svg'
import trash from '../../../../assets/fashion/TableFashionHeader/trash.svg'

const ORDERS_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`

interface FashionEditContent {
  handleEditSave: React.MutableRefObject<() => Promise<void>>
}

type SizeQuantities = { [size: string]: number }

interface FashionOrderProductItemInput {
  colorId: number;
  size: string;
  quantity: number;
}

interface ProductApiData {
  fashionProductId?: number
  name?: string
  totalItems?: number
  subtotal?: number
  discount?: number
  colorCount?: number
  items?: FashionOrderProductItemInput[]
  fashionProduct?: {
    id: number
    name: string
    price: number
    group?: {
      id: number
      name: string
      family?: {
        id: number
        name: string
      }
    }
  }
  family?: { name: string }
  group?: { name: string }
  sizeQuantities?: SizeQuantities
  colors?: string[]
  selectedColors?: number[]
  discountValue?: number
}

interface CompanyItemType {
  id: number
  companyName: string
}

interface UserItemType {
  id: number
  name: string
}

interface FreightType {
  value: string
  label: string
}

interface PaymentCondition {
  name: string
}


const calculateDiscountValue = (subtotal: number, discountPercentage: number): number => {
  return (subtotal * discountPercentage) / 100;
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const result = reader.result as string
      resolve(result)
    }
    reader.onerror = (error) => reject(error)
  })
}



const EditFashionOrder = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const { orderId } = useParams()
  const { handleEditSave, setIsSaving: setParentSaving } = useOutletContext<FashionEditContent & { setIsSaving?: (val: boolean) => void }>()
  
  const [addedProducts, setAddedProducts] = useState<ProductApiData[]>([])
  const [openProductIndex, setOpenProductIndex] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)

  const { setFeedback } = React.useContext(FeedbackContext)

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [fileUploadStatus, setFileUploadStatus] = useState<string>("")

  const [existingAttachments, setExistingAttachments] = useState<ExistingAttachment[]>([])

  const [formData, setFormData] = useState({
    supplier: { id: 0, companyName: '' } as CompanyItemType,
    seller: { id: 0, name: '' } as UserItemType,
    company: { id: 0, companyName: '' } as CompanyItemType,
    orderType: '',
    paymentCondition: '',
    shippingType: '',
    trackingCode: '',
    finalDateOfDelivery: '',
    startDateOfDelivery: '',
    extraInformation: '',
    quantityItems: '',
    quantityPieces: '',
    totalCost: '',
    deliveryTime: '',
    shippingCost: '',
    maximumBillingAmount: '',
  })

  const [freightTypes, setFreightTypes] = useState<FreightType[]>([
    { value: 'CIF', label: 'CIF' },
    { value: 'FOB', label: 'FOB' },
    { value: 'Cortesia', label: 'Cortesia' },
  ])
  const [paymentOptions, setPaymentOptions] = useState<string[]>(['30 dias', '30/60 dias', '30/60/90 dias', 'A vista'])
  const [newPaymentCondition, setNewPaymentCondition] = useState('')
  const [isAddingPayment, setIsAddingPayment] = useState(false)
  const [newshippingType, setNewshippingType] = useState('')
  const [isAddingShipping, setIsAddingShipping] = useState(false)

  const cleanCurrency = useCallback((value: any): number => {
    const stringValue = String(value || "")
    const number = Number(stringValue.replace(/[^0-9,-]+/g, "").replace(",", "."))
    return isNaN(number) ? 0 : number
  }, [])

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatForInput = useCallback((value: any): string => {
    if (value === null || value === undefined || value === "") {
      return "R$ 0,00"
    }

    const numericValue = typeof value === "string" ? Number(value.replace(/\D/g, "")) / 100 : Number(value)

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numericValue)
  }, [])

  const formatDate = useCallback((isoDateString: string | null | undefined): string => {
    if (!isoDateString) return ""
    return isoDateString.substring(0, 10)
  }, [])

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, [])

  const handleRemoveExistingAttachment = useCallback(async (attachmentId: number) => {
    if (window.confirm('Tem certeza que deseja remover este anexo?')) {
      setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId))
    }
  }, [orderId])

  const handleViewAttachment = useCallback(async (attachment: ExistingAttachment) => {
    if (attachment.filePath) {
      window.open(attachment.filePath, '_blank')
    }
  }, [orderId])

  const handleDownloadAttachment = useCallback(async (attachment: ExistingAttachment) => {
    if (attachment.filePath) {
      const link = document.createElement('a')
      link.href = attachment.filePath
      link.download = attachment.originalName
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }, [orderId])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024
    const validFiles: File[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (file.size > MAX_FILE_SIZE_BYTES) {
        alert(`Arquivo ${file.name} excede 200MB e será ignorado.`)
        continue
      }
      validFiles.push(file)
    }

    setSelectedFiles(prev => [...prev, ...validFiles])
    setFileUploadStatus("")
    event.target.value = ""
  }

  const handleRemoveFile = (indexToRemove: number) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove))
  }

  const normalizeProductData = useCallback((product: any): ProductApiData => {
    const sizeQuantities: SizeQuantities = {}

    if (product.items && Array.isArray(product.items)) {
      product.items.forEach((item: FashionOrderProductItemInput) => {
        if (item.size && item.quantity !== undefined) {
          sizeQuantities[item.size] =
            (sizeQuantities[item.size] || 0) + Number(item.quantity)
        }
      })
    }

    const totalItems = Object.values(sizeQuantities).reduce(
      (sum, qty) => sum + qty,
      0
    )

    const price = product.fashionProduct?.price || 0

    const subtotal = product.subtotal ?? totalItems * price

    const colorCount =
      product.colorCount ??
      (product.items
        ? new Set(product.items.map((item: any) => item.colorId)).size
        : 0)

    return {
      ...product,
      sizeQuantities,
      totalItems,
      colorCount,
      items: product.items || [],
      subtotal,
      fashionProduct: product.fashionProduct || {},
    }
  }, [])


  const fetchInitialData = async () => {
    return { paymentConditions: paymentOptions, shippingTypes: freightTypes }
  }

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${ORDERS_URL}/order/${orderId}`)
      return response.data
    } catch (error) {
      console.error('Erro ao buscar o pedido:', error)
      return null
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)

      await fetchInitialData()
      const orderData = await fetchOrder()

      if (orderData) {
        setFormData({
          supplier: {
            id: Number(orderData.supplierId),
            companyName: orderData.supplierDetails?.name || orderData.supplier || '',
          },
          seller: {
            id: orderData.sellerId || 0,
            name: orderData.sellerDetails?.name || orderData.seller || '',
          },
          company: {
            id: Number(orderData.clientId),
            companyName: orderData.clientDetails?.name || orderData.client || '',
          },
          orderType: orderData.orderType || '',
          paymentCondition: orderData.paymentTerm || (typeof orderData.paymentCondition === 'object' ? orderData.paymentCondition?.name : orderData.paymentCondition) || '',
          shippingType: (typeof orderData.shippingType === 'object' ? orderData.shippingType?.name : orderData.shippingType) || '',
          trackingCode: orderData.trackingCode || '',
          finalDateOfDelivery: formatDate(orderData.finalDateOfDelivery),
          startDateOfDelivery: formatDate(orderData.startDateOfDelivery || orderData.orderDate),
          extraInformation: orderData.extraInfo || orderData.extraInformation || '',
          quantityItems: orderData.quantityItems || '0',
          quantityPieces: orderData.quantityPieces || '0',
          totalCost: `R$ ${Number(orderData.totalCost || orderData.total || 0)
            .toFixed(2)
            .replace('.', ',')}`,
          deliveryTime: orderData.deliveryTime || '',
          shippingCost: orderData.shippingValue || orderData.shippingCost || '',
          maximumBillingAmount: orderData.maximumBillingAmount || '',
        })

        setExistingAttachments([])

        const fashionProducts = (orderData.items || [])
          .filter((item: any) => item.itemType === 'PRODUCT')
          .map((item: any) => {
            let extra: any = {}
            try { extra = item.extraInfo ? JSON.parse(item.extraInfo) : {} } catch { extra = {} }
            return normalizeProductData({
              fashionProductId: item.productId,
              fashionProduct: item.Product ? {
                id: item.Product.id,
                name: item.Product.name,
                price: item.Product.price || 0,
                group: item.Product.group || { id: extra.groupId },
              } : { id: item.productId, name: extra.fashionProductName || '', price: extra.fashionProductPrice || 0, group: { id: extra.groupId, name: extra.groupName, family: { id: 0, name: extra.familyName } } },
              discount: item.descount || 0,
              totalItems: item.quantity || 0,
              subtotal: item.priceKg || 0,
              items: extra.fashionItems || [],
              colorCount: extra.colorCount || 0,
              colors: extra.colors || [],
              selectedColors: extra.selectedColors || [],
            })
          })
        setAddedProducts(fashionProducts)
      }
      setLoading(false)
    }

    if (orderId) {
      loadData()
    }
  }, [orderId, formatDate, normalizeProductData])

  const handleAddPaymentCondition = async () => {
    if (newPaymentCondition && !paymentOptions.includes(newPaymentCondition)) {
      setPaymentOptions([...paymentOptions, newPaymentCondition])
      setFormData({ ...formData, paymentCondition: newPaymentCondition })
      setNewPaymentCondition('')
      setIsAddingPayment(false)
    }
  }

  const handleAddshippingType = async () => {
    if (newshippingType && !freightTypes.some((ft) => ft.value === newshippingType)) {
      setFreightTypes([...freightTypes, { value: newshippingType, label: newshippingType }])
      setFormData({ ...formData, shippingType: newshippingType })
      setNewshippingType('')
      setIsAddingShipping(false)
    }
  }

  const handleEditSaveOrder = useCallback(async () => {
    try {
      if (setParentSaving) {
        setParentSaving(true)
      }
      const groupedProductsMap = new Map();

      addedProducts.forEach(product => {
        const productId = product.fashionProduct?.id || product.fashionProductId;

        if (productId === undefined) {
          return;
        }

        if (groupedProductsMap.has(productId)) {
          const existingProduct = groupedProductsMap.get(productId)
          existingProduct.items = [...(existingProduct.items || []), ...(product.items || [])]
          existingProduct.totalItems += product.totalItems || 0
          existingProduct.subtotal += product.subtotal || 0
        } else {
          groupedProductsMap.set(productId, { ...product })
        }
      })

      const productsToSend = Array.from(groupedProductsMap.values())

      const formattedStartDate = formData.startDateOfDelivery ? new Date(formData.startDateOfDelivery).toISOString() : null
      const formattedFinalDate = formData.finalDateOfDelivery ? new Date(formData.finalDateOfDelivery).toISOString() : null

      const payload = {
        clientId: formData.company.id,
        supplierId: formData.supplier.id,
        sellerId: formData.seller.id,
        orderType: formData.orderType,
        paymentTerm: formData.paymentCondition,
        shippingType: formData.shippingType,
        trackingCode: formData.trackingCode,
        shippingCost: cleanCurrency(formData.shippingCost),
        totalCost: cleanCurrency(formData.totalCost),
        startDateOfDelivery: formattedStartDate,
        finalDateOfDelivery: formattedFinalDate,
        extraInfo: formData.extraInformation,
        quantityItems: formData.quantityItems,
        quantityPieces: formData.quantityPieces,
        deliveryTime: formData.deliveryTime,
        maximumBillingAmount: String(cleanCurrency(formData.maximumBillingAmount)),
        orderDate: formattedStartDate || new Date().toISOString(),
        items: productsToSend.map((product: any) => ({
          itemType: 'PRODUCT',
          productId: product.fashionProduct?.id || product.fashionProductId,
          quantity: product.totalItems || 0,
          descount: product.discount || 0,
          priceKg: product.subtotal || 0,
          extraInfo: JSON.stringify({
            fashionItems: product.items || [],
            colorCount: product.colorCount || 0,
            colors: product.colors || [],
            selectedColors: product.selectedColors || [],
            groupId: product.fashionProduct?.group?.id || product.group?.id,
            fashionProductName: product.fashionProduct?.name || product.name,
            fashionProductPrice: product.fashionProduct?.price || product.price,
            familyName: product.fashionProduct?.group?.family?.name || product.family?.name,
            groupName: product.fashionProduct?.group?.name || product.group?.name,
            collectionId: product.fashionProduct?.group?.family?.collectionId,
          }),
        })),
      } as any

      if (selectedFiles.length > 0) {
        try {
          setFileUploadStatus("Convertendo arquivos...")
          const attachmentsData = []

          for (const file of selectedFiles) {
            const base64Data = await fileToBase64(file)
            attachmentsData.push({
              file: base64Data,
              fileName: file.name
            })
          }

          payload.attachments = attachmentsData
          setFileUploadStatus("Arquivos processados, atualizando pedido...")
        } catch (error) {
          console.error('Erro ao converter arquivos para base64:', error)
          setFileUploadStatus("Erro ao processar arquivos")
          alert('Erro ao processar os arquivos anexos')
          return
        }
      }

      try {
        await axios.put(
          `${ORDERS_URL}/order/${orderId}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        )

        setFeedback({
          isOpen: true,
          message: 'Pedido atualizado com sucesso!',
          statusOk: true
        })

        setSelectedFiles([])
        setTimeout(() => setFileUploadStatus(""), 3000)
      } catch (error: any) {
        setFileUploadStatus("Erro ao atualizar pedido")

        setFeedback({
          isOpen: true,
          message: 'Erro ao atualizar o pedido',
          statusOk: false
        })
      } finally {
        if (setParentSaving) setParentSaving(false)
      }
    } catch (error) {
      setFileUploadStatus("Erro ao salvar pedido")
      setFeedback({
        isOpen: true,
        message: `Erro ao salvar pedido: ${(error as Error).message || 'Erro desconhecido'}`,
        statusOk: false
      })
      if (setParentSaving) setParentSaving(false)
    }
  }, [formData, addedProducts, orderId, cleanCurrency, selectedFiles, setFeedback, setParentSaving])

  useEffect(() => {
    if (handleEditSave.current) {
      handleEditSave.current = handleEditSaveOrder
    }
  }, [handleEditSaveOrder, handleEditSave])

  const handleRemoveProduct = (indexToRemove: number) => {
    setAddedProducts((prev) => prev.filter((_, i) => i !== indexToRemove))
    setOpenProductIndex(null)
  }

  const handleUpdateProduct = (oldProduct: any, updatedProduct: any) => {
    if (openProductIndex !== null) {
      const normalizedProduct = normalizeProductData(updatedProduct)

      setAddedProducts((prevProducts) => {
        const newProducts = [...prevProducts]
        const existingProduct = newProducts[openProductIndex]

        newProducts[openProductIndex] = {
          ...existingProduct,
          ...normalizedProduct,
        }

        return newProducts
      })

      setOpenProductIndex(null)
    }
  }

  const handleNewProductAdded = (newProduct: ProductApiData) => {
    const normalizedProduct = normalizeProductData(newProduct)

    setAddedProducts((prevProducts) => [...prevProducts, normalizedProduct])
    setShowAddProduct(false)
    setOpenProductIndex(null)
  }

  useEffect(() => {
    let totalItemsCount = 0
    let totalPiecesSum = 0
    let totalCostSum = 0
    let totalDiscountValue = 0

    addedProducts.forEach((product) => {
      totalItemsCount += 1

      const productTotalItems = product.sizeQuantities
        ? Object.values(product.sizeQuantities).reduce((sum, qty) => sum + qty, 0)
        : product.totalItems || 0

      totalPiecesSum += productTotalItems

      const subtotalWithoutDiscount = product.subtotal || 0

      let discountValue = 0
      if (product.discount && product.discount > 0) {
        const originalSubtotal = subtotalWithoutDiscount / (1 - product.discount / 100)
        discountValue = calculateDiscountValue(originalSubtotal, product.discount)
      }

      totalDiscountValue += discountValue
      totalCostSum += subtotalWithoutDiscount
    })

    setFormData((prevState) => ({
      ...prevState,
      quantityItems: String(totalItemsCount),
      quantityPieces: String(totalPiecesSum),
      totalCost: formatCurrency(totalCostSum),
    }))
  }, [addedProducts])

  const handleSupplierChange = (supplier: CompanyItemType) => {
    setFormData((prevState) => ({ ...prevState, supplier }))
  }

  const handleCompanyChange = (company: CompanyItemType) => {
    setFormData((prevState) => ({ ...prevState, company }))
  }

  const handleSellerChange = (seller: UserItemType) => {
    setFormData((prevState) => ({ ...prevState, seller }))
  }

  const getSafeValue = (value: any): string => {
    if (typeof value === 'object' && value !== null) {
      if ('id' in value && 'name' in value) {
        return value.name || '-'
      }
      return '-'
    }
    return String(value) || '-'
  }

  const calculateDiscountMonetaryValue = (product: ProductApiData): string => {
    if (!product.discount || product.discount <= 0 || !product.subtotal) {
      return "-"
    }

    const originalSubtotal = product.subtotal / (1 - product.discount / 100)

    const discountValue = calculateDiscountValue(originalSubtotal, product.discount)

    return formatCurrency(discountValue)
  }

  return (
    <>
      <BreadCrumble>
        <FirstText>Pedidos fashion</FirstText>
        <SecondText>&gt; Editar pedido</SecondText>
      </BreadCrumble>

      <Card>
        <FormGrid style={{ marginTop: '0' }}>
          <FormGroup>
            <CompanySelect
              label='Fornecedor'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`}
              displayField='companyName'
              queryParams={{ companyType: 2 }}
              value={formData.supplier}
              onChange={handleSupplierChange}
            />
            <InfoLink>
              <Info size={16} />
              <span>Mais informações</span>
            </InfoLink>
          </FormGroup>

          <FormGroup>
            <VendorSelect
              label='Vendedor'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll`}
              displayField='name'
              value={formData.seller}
              onChange={handleSellerChange}
            />
            <InfoLink>
              <Info size={16} />
              <span>Mais informações</span>
            </InfoLink>
          </FormGroup>

          <FormGroup>
            <CompanySelect
              label='Cliente'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`}
              displayField='companyName'
              queryParams={{ companyType: 1 }}
              value={formData.company}
              onChange={handleCompanyChange}
            />
            <InfoLink>
              <Info size={16} />
              <span>Mais informações</span>
            </InfoLink>
          </FormGroup>
        </FormGrid>

        <Separator />

        <CollapsibleHeader onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
          <Title>Detalhes do pedido</Title>
          <Button>{isDetailsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</Button>
        </CollapsibleHeader>

        {isDetailsOpen && (
          <>
            <FormGrid>
              <FormGroup>
                <Label>Tipo de pedido</Label>
                <Select
                  value={formData.orderType}
                  onChange={(e) => setFormData({ ...formData, orderType: e.target.value })}
                >
                  <option value=''>Selecione o tipo de pedido</option>
                  <option value='Venda'>Venda</option>
                  <option value='Reposição'>Reposição</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Data inicio da entrega</Label>
                <InputText
                  type='date'
                  value={formData.startDateOfDelivery}
                  max="9999-12-31"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      startDateOfDelivery: e.target.value,
                    })
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Data final da entrega</Label>
                <InputText
                  type='date'
                  value={formData.finalDateOfDelivery}
                  max="9999-12-31"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      finalDateOfDelivery: e.target.value,
                    })
                  }
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Condição de pagamento</Label>
                {isAddingPayment ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <InputText
                      type="text"
                      value={newPaymentCondition}
                      onChange={(e) => setNewPaymentCondition(e.target.value)}
                      placeholder="Nova condição"
                    />
                    <Button type="button" onClick={handleAddPaymentCondition}>OK</Button>
                    <Button type="button" onClick={() => setIsAddingPayment(false)}>X</Button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Select
                      value={formData.paymentCondition}
                      onChange={(e) => setFormData({ ...formData, paymentCondition: e.target.value })}
                    >
                      <option value="">Selecione</option>
                      {paymentOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </Select>
                    <Plus size={18} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => setIsAddingPayment(true)} />
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Tipo de frete</Label>
                {isAddingShipping ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <InputText
                      type="text"
                      value={newshippingType}
                      onChange={(e) => setNewshippingType(e.target.value)}
                      placeholder="Novo tipo de frete"
                    />
                    <Button type="button" onClick={handleAddshippingType}>OK</Button>
                    <Button type="button" onClick={() => setIsAddingShipping(false)}>X</Button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Select
                      value={formData.shippingType}
                      onChange={(e) => setFormData({ ...formData, shippingType: e.target.value })}
                    >
                      <option value="">Selecione</option>
                      {freightTypes.map((ft) => (
                        <option key={ft.value} value={ft.value}>{ft.label}</option>
                      ))}
                    </Select>
                    <Plus size={18} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => setIsAddingShipping(true)} />
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Valor do frete</Label>
                <InputText
                  type="text"
                  value={formatForInput(formData.shippingCost)}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, "")
                    setFormData({ ...formData, shippingCost: cleanedValue })
                  }}
                  placeholder="Digite o valor do frete"
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Código de rastreio</Label>
                <InputText
                  type="text"
                  value={formData.trackingCode}
                  onChange={(e) => setFormData({ ...formData, trackingCode: e.target.value })}
                  placeholder="Digite o código de rastreio"
                />
              </FormGroup>

              <FormGroup>
                <Label>Anexos</Label>
                <AttachmentManager
                  selectedFiles={selectedFiles}
                  existingAttachments={existingAttachments}
                  onFileChange={handleFileChange}
                  onRemoveFile={handleRemoveFile}
                  onViewAttachment={handleViewAttachment}
                  onDownloadAttachment={handleDownloadAttachment}
                  onRemoveExistingAttachment={handleRemoveExistingAttachment}
                  formatFileSize={formatFileSize}
                />
              </FormGroup>

              <FormGroup>
                <Label>Valor máximo de faturamento</Label>
                <InputText
                  type="text"
                  value={formatForInput(formData.maximumBillingAmount)}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, "")
                    setFormData({
                      ...formData,
                      maximumBillingAmount: cleanedValue,
                    })
                  }}
                  className="w-full rounded bg-gray-100 p-2"
                  placeholder="Digite o valor máximo"
                />
              </FormGroup>
            </FormGrid>
            <FormGrid>
              <FormGroup>
                <Label>Informações adicionais</Label>
                <InputText
                  type='text'
                  value={formData.extraInformation}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      extraInformation: e.target.value,
                    })
                  }
                />
              </FormGroup>
            </FormGrid>

          </>
        )}

        <Separator />

        <Summary>
          <SummaryItem>
            <SummaryLabel>Quantidade de itens:</SummaryLabel>
            <SummaryValue>{formData.quantityItems}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Quantidade de peças:</SummaryLabel>
            <SummaryValue>{formData.quantityPieces}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total:</SummaryLabel>
            <SummaryValue>{formData.totalCost}</SummaryValue>
          </SummaryItem>
        </Summary>
      </Card>

      <>
        {Array.isArray(addedProducts) && addedProducts.length > 0 && (
          <>
            {addedProducts.map((product, index) => {
              const productName = product.fashionProduct?.name || product.name || "-"
              const familyName = product.fashionProduct?.group?.family?.name || product.family?.name || "-"
              const groupName = product.fashionProduct?.group?.name || product.group?.name || "-"

              const priceValue =
                typeof product.fashionProduct?.price === "number"
                  ? `R$ ${product.fashionProduct.price.toFixed(2).replace(".", ",")}`
                  : "-"

              const subtotalValue =
                typeof product.subtotal === "number" ? formatCurrency(product.subtotal) : "-"

              const currentTotalItems = product.sizeQuantities
                ? Object.values(product.sizeQuantities).reduce((sum, qty) => sum + qty, 0)
                : product.totalItems || 0

              const productFields = [
                { label: 'Produto', value: productName, showIcon: true },
                { label: 'Família', value: familyName, showIcon: true },
                {
                  label: "Cores",
                  value: product.colorCount ?? "-",
                  showIcon: true,
                },
                { label: "Precificação", value: priceValue, showIcon: true },
                {
                  label: "Quantidade de peças",
                  value: currentTotalItems ?? "-",
                  showIcon: true,
                },
                {
                  label: "Desconto",
                  value: calculateDiscountMonetaryValue(product),
                  showIcon: true,
                },
                { label: "Subtotal", value: subtotalValue, showIcon: false },
              ]

              return (
                <Card key={product.fashionProductId || index}>
                  <ProductShort>
                    <ProductDiv>
                      {productFields.map(({ label, value, showIcon }, i) => (
                        <ShortLabel key={i}>
                          <ProductFieldLabel>{label}:</ProductFieldLabel> {getSafeValue(value)}{" "}
                          {showIcon && (
                            <img style={{ marginLeft: "0.5rem" }} src={rectangle || "/placeholder.svg"} alt="" />
                          )}
                        </ShortLabel>
                      ))}
                      <ProductFieldsContainer>
                        <img
                          src={trash}
                          alt='Remover'
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRemoveProduct(index)}
                        />
                        <Button
                          onClick={() => {
                            setOpenProductIndex(openProductIndex === index ? null : index)
                          }}
                        >
                          {openProductIndex === index ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </Button>
                      </ProductFieldsContainer>
                    </ProductDiv>
                  </ProductShort>

                  {openProductIndex === index && (
                    <AddProductForm
                      order={addedProducts}
                      setOrder={setAddedProducts}
                      product={product}
                      onClose={() => {
                        setOpenProductIndex(null)
                      }}
                      onProductUpdated={handleUpdateProduct}
                      onRemoveProduct={handleRemoveProduct}
                      isEditing={true}
                      onProductAdded={handleNewProductAdded}
                    />
                  )}
                </Card>
              )
            })}
          </>
        )}
      </>

      <AddProductContainer>
        {showAddProduct && (
          <AddProductForm
            order={addedProducts}
            setOrder={setAddedProducts}
            product={undefined}
            onClose={() => setShowAddProduct(false)}
            onRemoveProduct={handleRemoveProduct}
            onProductUpdated={handleUpdateProduct}
            isEditing={false}
            onProductAdded={handleNewProductAdded}
          />
        )}
        <AddButton
          onClick={() => {
            setShowAddProduct(!showAddProduct)
            setOpenProductIndex(null)
          }}
        >
          <AddButtonImg src={plus} alt='Adicionar' />
          <AddButtonText>Adicionar produto</AddButtonText>
        </AddButton>
      </AddProductContainer>
    </>
  )
}

export default EditFashionOrder